/**
 * AtlanticRouteMap — Institutional SVG map of the Uetze → Praia (Cape Verde) corridor.
 * Stylised (not geographically exact), tuned for the MIONGA Navy · Oak · Gold palette.
 * Includes animated great-circle arc, pulsing pins, coordinate readouts and compass rose.
 */
import React from "react";
import { motion } from "framer-motion";

/* SVG viewBox: 1000 x 560. Coordinates chosen for editorial clarity, not GIS precision. */
const UETZE   = { x: 720, y: 165, lat: "52.47°N", lon: "10.21°E", city: "Uetze", region: "Niedersachsen · Germany" };
const PRAIA   = { x: 355, y: 425, lat: "14.93°N", lon: "23.51°W", city: "Praia",  region: "Cabo Verde · Atlantic" };

export const AtlanticRouteMap = ({ tokens, t, className = "" }) => {
  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}
      data-testid="mionga-atlantic-map"
      style={{
        aspectRatio: "1000/560",
        background:
          "radial-gradient(1200px 700px at 30% 60%, #0F2338 0%, #0B1A2E 55%, #081426 100%)",
        border: `1px solid ${tokens.border}`,
        boxShadow: "0 40px 100px -30px rgba(0,0,0,0.75), 0 2px 0 rgba(212,168,87,0.06) inset",
      }}>
      <svg viewBox="0 0 1000 560" className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="oceanGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0F2338" />
            <stop offset="100%" stopColor="#081426" />
          </linearGradient>
          <linearGradient id="routeGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%"  stopColor={tokens.gold} stopOpacity="0.05" />
            <stop offset="45%" stopColor={tokens.gold} stopOpacity="0.9" />
            <stop offset="100%" stopColor={tokens.gold} stopOpacity="0.05" />
          </linearGradient>
          <radialGradient id="pinPulse" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={tokens.gold} stopOpacity="0.55" />
            <stop offset="60%" stopColor={tokens.gold} stopOpacity="0.20" />
            <stop offset="100%" stopColor={tokens.gold} stopOpacity="0" />
          </radialGradient>
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* Ocean */}
        <rect width="1000" height="560" fill="url(#oceanGrad)" />

        {/* Latitude/Longitude grid — subtle */}
        {[80, 160, 240, 320, 400, 480].map((y) => (
          <line key={"h"+y} x1="0" x2="1000" y1={y} y2={y}
            stroke={tokens.gold} strokeOpacity="0.055" strokeWidth="0.6" strokeDasharray="2 6" />
        ))}
        {[100, 240, 380, 520, 660, 800, 940].map((x) => (
          <line key={"v"+x} x1={x} x2={x} y1="0" y2="560"
            stroke={tokens.gold} strokeOpacity="0.055" strokeWidth="0.6" strokeDasharray="2 6" />
        ))}

        {/* Continents — stylised silhouettes (Europe top-right, W-Africa right, Cape Verde islands) */}
        {/* Europe / Iberia / France / Germany block */}
        <path d="M 560,60 L 640,55 L 720,60 L 780,80 L 830,110 L 860,140 L 880,170 L 895,210
                 L 900,255 L 890,290 L 855,300 L 820,285 L 780,270 L 745,255 L 720,240
                 L 700,220 L 685,205 L 665,200 L 640,215 L 615,235 L 595,240
                 L 575,220 L 560,190 L 555,150 L 555,110 Z"
          fill="#122C46" stroke={tokens.gold} strokeOpacity="0.28" strokeWidth="0.9" />
        {/* North / West Africa */}
        <path d="M 720,250 L 780,265 L 830,280 L 875,300 L 900,335 L 915,375 L 920,420
                 L 905,460 L 875,495 L 830,520 L 780,530 L 730,525 L 690,505 L 665,470
                 L 655,430 L 665,395 L 685,365 L 700,335 L 715,300 Z"
          fill="#132A44" stroke={tokens.gold} strokeOpacity="0.24" strokeWidth="0.8" />
        {/* Iberian peninsula tail */}
        <path d="M 540,225 L 585,235 L 610,255 L 620,280 L 605,295 L 570,290 L 545,275 L 530,255 Z"
          fill="#122C46" stroke={tokens.gold} strokeOpacity="0.24" strokeWidth="0.8" />
        {/* Cape Verde archipelago — cluster of small islands */}
        <g>
          <ellipse cx="345" cy="405" rx="8"  ry="4"  fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
          <ellipse cx="360" cy="420" rx="7"  ry="3.5" fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
          <ellipse cx="375" cy="440" rx="6"  ry="3"  fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
          <ellipse cx="340" cy="432" rx="5"  ry="2.5" fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
          <ellipse cx="330" cy="418" rx="4"  ry="2"  fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
          <ellipse cx="325" cy="400" rx="5"  ry="2.5" fill="#1E3E60" stroke={tokens.gold} strokeOpacity="0.35" strokeWidth="0.6" />
        </g>

        {/* Great-circle arc from Uetze to Praia (animated dashed line) */}
        <motion.path
          d={`M ${UETZE.x} ${UETZE.y} Q 480 200 ${PRAIA.x} ${PRAIA.y}`}
          fill="none" stroke="url(#routeGrad)" strokeWidth="2.4"
          strokeDasharray="8 6" strokeLinecap="round"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease: "easeInOut" }}
          viewport={{ once: true }} />
        {/* Route flow — small moving dot along the arc */}
        <circle r="4" fill={tokens.gold} filter="url(#softGlow)">
          <animateMotion dur="6s" repeatCount="indefinite"
            path={`M ${UETZE.x} ${UETZE.y} Q 480 200 ${PRAIA.x} ${PRAIA.y}`} />
          <animate attributeName="opacity" values="0;1;1;0" dur="6s" repeatCount="indefinite" />
        </circle>

        {/* UETZE pin */}
        <g transform={`translate(${UETZE.x},${UETZE.y})`}>
          <circle r="26" fill="url(#pinPulse)">
            <animate attributeName="r" values="18;30;18" dur="3.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.65;0.15;0.65" dur="3.2s" repeatCount="indefinite" />
          </circle>
          <circle r="6.5" fill={tokens.gold} stroke="#FBFAF6" strokeWidth="2" filter="url(#softGlow)" />
          <circle r="2" fill="#0B1A2E" />
        </g>

        {/* PRAIA pin */}
        <g transform={`translate(${PRAIA.x},${PRAIA.y})`}>
          <circle r="26" fill="url(#pinPulse)">
            <animate attributeName="r" values="18;30;18" dur="3.2s" begin="1.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.65;0.15;0.65" dur="3.2s" begin="1.6s" repeatCount="indefinite" />
          </circle>
          <circle r="6.5" fill={tokens.gold} stroke="#FBFAF6" strokeWidth="2" filter="url(#softGlow)" />
          <circle r="2" fill="#0B1A2E" />
        </g>

        {/* Compass rose (bottom-left) */}
        <g transform="translate(60,490)" opacity="0.55">
          <circle r="26" fill="none" stroke={tokens.gold} strokeWidth="0.6" strokeOpacity="0.55" />
          <polygon points="0,-22 4,0 0,22 -4,0" fill={tokens.gold} fillOpacity="0.7" />
          <text y="-30" textAnchor="middle" fontSize="9" fill={tokens.gold}
            style={{ letterSpacing: "0.18em", fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>N</text>
        </g>
      </svg>

      {/* Overlaid HTML labels — richer typography than SVG text */}
      {/* UETZE card */}
      <div className="absolute" style={{ left: "68%", top: "16%" }}>
        <div className="translate-x-3 -translate-y-2 backdrop-blur-md rounded-md px-3.5 py-2"
          style={{ background: "rgba(11,26,46,0.82)", border: `1px solid ${tokens.gold}55`, minWidth: 200 }}
          data-testid="map-pin-uetze">
          <div className="flex items-center gap-2">
            <span className="text-[13px]" role="img" aria-label="Germany">🇩🇪</span>
            <div>
              <div className="font-display font-semibold text-[13px] leading-none" style={{ color: tokens.white }}>{UETZE.city}</div>
              <div className="text-[9.5px] uppercase tracking-[0.22em] mt-1" style={{ color: tokens.oakSoft }}>{UETZE.region}</div>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t flex items-center gap-3 text-[9px]"
            style={{ borderColor: `${tokens.gold}33`, color: tokens.gold, letterSpacing: "0.14em" }}>
            <span>{UETZE.lat}</span><span>·</span><span>{UETZE.lon}</span>
          </div>
        </div>
      </div>

      {/* PRAIA card */}
      <div className="absolute" style={{ left: "22%", top: "70%" }}>
        <div className="-translate-x-16 backdrop-blur-md rounded-md px-3.5 py-2"
          style={{ background: "rgba(11,26,46,0.82)", border: `1px solid ${tokens.gold}55`, minWidth: 200 }}
          data-testid="map-pin-praia">
          <div className="flex items-center gap-2">
            <span className="text-[13px]" role="img" aria-label="Cabo Verde">🇨🇻</span>
            <div>
              <div className="font-display font-semibold text-[13px] leading-none" style={{ color: tokens.white }}>{PRAIA.city}</div>
              <div className="text-[9.5px] uppercase tracking-[0.22em] mt-1" style={{ color: tokens.oakSoft }}>{PRAIA.region}</div>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t flex items-center gap-3 text-[9px]"
            style={{ borderColor: `${tokens.gold}33`, color: tokens.gold, letterSpacing: "0.14em" }}>
            <span>{PRAIA.lat}</span><span>·</span><span>{PRAIA.lon}</span>
          </div>
        </div>
      </div>

      {/* Legend chip (top-left) */}
      <div className="absolute top-4 left-4 backdrop-blur-md rounded-full px-3.5 py-1.5"
        style={{ background: "rgba(11,26,46,0.75)", border: `1px solid ${tokens.gold}55` }}>
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: tokens.gold }}>
          {t.eyebrow}
        </span>
      </div>

      {/* Corridor read-out (bottom-right) */}
      <div className="absolute right-4 bottom-4 backdrop-blur-md rounded-md px-4 py-2.5"
        style={{ background: "rgba(11,26,46,0.82)", border: `1px solid ${tokens.gold}55` }}
        data-testid="map-route-readout">
        <div className="flex items-center gap-6">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: tokens.oakSoft }}>{t.routeLabel}</div>
            <div className="text-[12.5px] font-semibold mt-0.5" style={{ color: tokens.white }}>{t.routeValue}</div>
          </div>
          <div className="w-px h-8" style={{ background: `${tokens.gold}55` }} />
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: tokens.oakSoft }}>{t.distanceLabel}</div>
            <div className="text-[12.5px] font-semibold mt-0.5" style={{ color: tokens.gold }}>{t.distanceValue}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AtlanticRouteMap;
